var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ConsentStatus, IdentifiedPatientState, primaryConsentTypes } from "healthex-common/consent.dtos";
import { isPatientConsentEnrolledActionNeeded } from "healthex-common/patient.sharing.utils";
import { ScreenshotMetadataStatus } from "healthex-common/screenshot.dtos";
import { ProjectType, StudyPatientConsentStatusSearch, StudyStatus } from "healthex-common/study.dtos";
import { willNeverHappen } from "healthex-common/utils";
import { action, computed, observable, runInAction } from "mobx";
import { Disposer } from "../Infra/Disposer";
import { getObservableT } from "../Infra/i18next";
import { NOT_LOADED } from "../Infra/RemoteData";
import { getPatientDateString, getPatientStatusChip, getPatientStatusSectionTitle } from "../patient.utils";
import { getTranslatedStudyText } from "./TranslationService";
import { openAsyncWindow } from "./WindowHelpers";
const t = getObservableT("patients");
export class PatientResearchStudyDetailsViewModelProps {
    constructor(container, modalStudyId) {
        this.container = container;
        this.modalStudyId = modalStudyId;
    }
}
export class PatientResearchStudyDetailsViewModel {
    constructor(props) {
        var _a;
        this.modalStudyId = null;
        this.refreshConsentTimerId = null;
        this.disposer = new Disposer();
        this.studyRevokeConsentDialogOpen = false;
        this.studyRevokeConsentStudyId = null;
        this.byStudyConsentRequest = NOT_LOADED;
        this.patientStudyConsent = NOT_LOADED;
        this.isRevokeConsentButtonLoading = false;
        this.isOpeningProofOfConsent = false;
        this.studyManager = props.container.studyManager;
        this.patientResearchDashboardUIManager = props.container.patientResearchDashboardUIManager;
        this.patientConsentUIManager = props.container.patientConsentUIManager;
        this.routingUIManager = props.container.routingUIManager;
        this.patientService = props.container.patientService;
        this.authManager = props.container.authManager;
        this.browserLanguageUIManager = props.container.browserLanguageUIManager;
        this.modalStudyId = (_a = props.modalStudyId) !== null && _a !== void 0 ? _a : null;
    }
    run() {
        this.disposer.reaction(() => this.studyId, async () => {
            await this.loadConsentRequest();
        }, { fireImmediately: true });
        this.disposer.reaction(() => this.activeConsent, async () => {
            await this.loadPatientStudyConsent();
        }, { fireImmediately: true });
        this.disposer.reaction(() => this.patientStudyConsent, async () => {
            await this.startRefreshConsentTimerIfNeeded();
        }, { fireImmediately: true });
    }
    stop() {
        this.clearRefreshConsentTimerIfNeeded();
        this.disposer.dispose();
    }
    clearRefreshConsentTimerIfNeeded() {
        if (this.refreshConsentTimerId !== null) {
            window.clearInterval(this.refreshConsentTimerId);
        }
    }
    async loadConsentRequest() {
        var _a, _b, _c;
        if (this.studyId === null)
            return;
        if (this.authManager.id === null)
            return;
        const { results } = await this.patientService.postSearchForPatientStudyConsents(this.authManager.id, {
            consentType: primaryConsentTypes,
            includeConsentingByLink: true,
            patientState: (_a = this.authManager.state) !== null && _a !== void 0 ? _a : undefined,
            distinctPending: true,
            includePatientStudyConsentStatuses: [ConsentStatus.OPTED_IN, ConsentStatus.OPTED_OUT],
            studyId: (_b = this.studyId) !== null && _b !== void 0 ? _b : undefined,
            study: {
                excludeStatuses: [StudyStatus.COMPLETED, StudyStatus.WITHDRAWN],
                excludeDeId: true,
                patientConsentStatusSearch: StudyPatientConsentStatusSearch.INCLUDE_ALL_OPTED_IN,
                patientId: (_c = this.authManager.id) !== null && _c !== void 0 ? _c : undefined,
            },
        }, 0);
        if (results.length === 0) {
            this.byStudyConsentRequest = null;
            return;
        }
        this.byStudyConsentRequest = {
            study: results[0].study,
            consents: results.flatMap((result) => result.consents),
        };
    }
    async loadPatientStudyConsent() {
        if (this.studyId === null)
            return;
        if (this.authManager.id === null)
            return;
        if (this.byStudyConsentRequest === NOT_LOADED || this.byStudyConsentRequest === null)
            return;
        if (this.activeConsent === null)
            return;
        this.patientStudyConsent = await this.patientService.getPatientStudyConsent(this.authManager.id, this.studyId, true);
    }
    async startRefreshConsentTimerIfNeeded() {
        if (this.patientStudyConsent === NOT_LOADED || this.patientStudyConsent === null)
            return;
        this.clearRefreshConsentTimerIfNeeded();
        this.refreshConsentTimerId = window.setInterval(() => {
            if (this.proofOfConsentStatus === ScreenshotMetadataStatus.JOB_QUEUED ||
                this.proofOfConsentStatus === ScreenshotMetadataStatus.IN_PROGRESS) {
                void this.loadPatientStudyConsent();
            }
            else {
                this.clearRefreshConsentTimerIfNeeded();
            }
        }, 5000);
    }
    get studyId() {
        var _a, _b, _c;
        return (_c = (_a = this.modalStudyId) !== null && _a !== void 0 ? _a : (_b = this.routingUIManager.currentRouteParams) === null || _b === void 0 ? void 0 : _b.studyId) !== null && _c !== void 0 ? _c : null;
    }
    get isConsentRequestLoaded() {
        return this.byStudyConsentRequest !== NOT_LOADED;
    }
    get allConsents() {
        if (this.byStudyConsentRequest === NOT_LOADED || this.byStudyConsentRequest === null)
            return [];
        return this.byStudyConsentRequest.consents;
    }
    get activeConsent() {
        var _a;
        return (_a = this.allConsents.find((consent) => consent.state === IdentifiedPatientState.ENROLLED && !consent.pending)) !== null && _a !== void 0 ? _a : null;
    }
    get study() {
        var _a, _b;
        if (this.byStudyConsentRequest === NOT_LOADED)
            return null;
        return (_b = (_a = this.byStudyConsentRequest) === null || _a === void 0 ? void 0 : _a.study) !== null && _b !== void 0 ? _b : null;
    }
    get patientFriendlyTitle() {
        var _a, _b, _c, _d;
        if (!this.study)
            return null;
        const translatedTitle = getTranslatedStudyText(this.study, "patientFriendlyTitle", this.browserLanguageUIManager.currentLanguage);
        return (_d = (_b = translatedTitle !== null && translatedTitle !== void 0 ? translatedTitle : (_a = this.study) === null || _a === void 0 ? void 0 : _a.patientFriendlyTitle) !== null && _b !== void 0 ? _b : (_c = this.study) === null || _c === void 0 ? void 0 : _c.title) !== null && _d !== void 0 ? _d : null;
    }
    get reasonForRequest() {
        if (!this.study)
            return null;
        const translatedRequestReason = getTranslatedStudyText(this.study, "purposeOfDataBriefEducation", this.browserLanguageUIManager.currentLanguage);
        return translatedRequestReason || null;
    }
    get patientFriendlySummary() {
        var _a, _b, _c, _d;
        if (!this.study)
            return null;
        const translatedSummary = getTranslatedStudyText(this.study, "patientFriendlySummary", this.browserLanguageUIManager.currentLanguage);
        return (_d = (_b = translatedSummary !== null && translatedSummary !== void 0 ? translatedSummary : (_a = this.study) === null || _a === void 0 ? void 0 : _a.patientFriendlySummary) !== null && _b !== void 0 ? _b : (_c = this.study) === null || _c === void 0 ? void 0 : _c.briefSummary) !== null && _d !== void 0 ? _d : null;
    }
    get description() {
        return this.isAIProject ? this.reasonForRequest : this.patientFriendlySummary || this.reasonForRequest;
    }
    get sponsorOrganizationName() {
        var _a, _b;
        return (_b = (_a = this.study) === null || _a === void 0 ? void 0 : _a.sponsorOrganizationName) !== null && _b !== void 0 ? _b : null;
    }
    get consentFromPatientStudyConsent() {
        var _a;
        if (this.patientStudyConsent === null || this.patientStudyConsent === NOT_LOADED || this.activeConsent === null) {
            return null;
        }
        const activeConsent = this.activeConsent;
        return (_a = this.patientStudyConsent.consents.find((consent) => consent.consentType === activeConsent.consentType)) !== null && _a !== void 0 ? _a : null;
    }
    get proofOfConsentStatus() {
        var _a, _b;
        if (this.consentFromPatientStudyConsent === null)
            return null;
        return (_b = (_a = this.consentFromPatientStudyConsent) === null || _a === void 0 ? void 0 : _a.proofOfConsentPdfStatus) !== null && _b !== void 0 ? _b : null;
    }
    get proofOfConsentInProgress() {
        return (this.proofOfConsentStatus === ScreenshotMetadataStatus.JOB_QUEUED ||
            this.proofOfConsentStatus === ScreenshotMetadataStatus.IN_PROGRESS);
    }
    get proofOfConsentStatusText() {
        if (this.study === null)
            return null;
        if (this.proofOfConsentStatus === null)
            return null;
        switch (this.proofOfConsentStatus) {
            case ScreenshotMetadataStatus.JOB_QUEUED:
            case ScreenshotMetadataStatus.IN_PROGRESS:
                return t("Your proof of consent is loading and this may take several minutes");
            case ScreenshotMetadataStatus.ERROR:
                return t("The proof of consent cannot be downloaded. Please try again later or contact {{ primaryContactName }} at ", {
                    primaryContactName: this.study.sitePrimaryContactName,
                });
            case ScreenshotMetadataStatus.COMPLETED:
                return null;
        }
        return willNeverHappen(this.proofOfConsentStatus);
    }
    get proofOfConsentUrl() {
        var _a, _b;
        return (_b = (_a = this.consentFromPatientStudyConsent) === null || _a === void 0 ? void 0 : _a.proofOfConsentPdfUrl) !== null && _b !== void 0 ? _b : "";
    }
    async openProofOfConsent() {
        if (!this.proofOfConsentUrl)
            return;
        this.isOpeningProofOfConsent = true;
        try {
            const url = this.proofOfConsentUrl;
            await openAsyncWindow(async () => {
                const response = await fetch(url);
                if (!response.ok)
                    throw new Error(`Failed to fetch consent PDF: ${response.status}`);
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob.slice(0, blob.size, "application/pdf"));
                setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                return blobUrl;
            });
        }
        catch (e) {
            console.error("[PatientResearchStudyDetails] Failed to open proof of consent", e);
        }
        finally {
            runInAction(() => {
                this.isOpeningProofOfConsent = false;
            });
        }
    }
    get showProofOfConsentButton() {
        var _a;
        if (this.consentFromPatientStudyConsent === null)
            return false;
        return ((_a = this.consentFromPatientStudyConsent) === null || _a === void 0 ? void 0 : _a.consentStatus) !== ConsentStatus.OPTED_OUT;
    }
    get proofOfConsentButtonEnabled() {
        return this.proofOfConsentStatus === ScreenshotMetadataStatus.COMPLETED && !!this.proofOfConsentUrl;
    }
    get isProofOfConsentError() {
        return this.proofOfConsentStatus === ScreenshotMetadataStatus.ERROR;
    }
    get isAIProject() {
        if (!this.study)
            return false;
        return this.study.projectType === ProjectType.AI_DATA_USE;
    }
    get bannerInfo() {
        var _a, _b;
        if (this.study === null)
            return null;
        return {
            orgLogo: (_a = this.study.organizationIconUrl) !== null && _a !== void 0 ? _a : null,
            orgLogoRectangular: (_b = this.study.organizationRectangularIconUrl) !== null && _b !== void 0 ? _b : null,
        };
    }
    consentBucket(consent) {
        if (!!consent.pending ||
            consent.state === IdentifiedPatientState.ENROLLING ||
            consent.state === IdentifiedPatientState.ENROLLING_INTERESTED ||
            consent.state === IdentifiedPatientState.ENROLLING_CONTACTED ||
            (consent.state === IdentifiedPatientState.ENROLLED && isPatientConsentEnrolledActionNeeded(consent))) {
            return "actionNeeded";
        }
        if (consent.state === IdentifiedPatientState.ENROLLED)
            return "active";
        if (consent.state === IdentifiedPatientState.DECLINED_ENROLLMENT ||
            consent.state === IdentifiedPatientState.REMOVED ||
            consent.state === IdentifiedPatientState.REVOKED_ENROLLMENT) {
            return "inactive";
        }
        return null;
    }
    buildConsentSection(consent, bucket) {
        const isInactiveReReviewable = consent.state === IdentifiedPatientState.DECLINED_ENROLLMENT || consent.state === IdentifiedPatientState.REVOKED_ENROLLMENT;
        return {
            key: consent.id,
            sectionTitle: getPatientStatusSectionTitle(consent),
            chip: getPatientStatusChip(consent),
            title: this.patientFriendlyTitle,
            date: getPatientDateString(consent.timestamp),
            description: this.description,
            showUpdateRecords: !this.isAIProject && consent.state === IdentifiedPatientState.ENROLLED && !!consent.updateRecordsNeeded,
            showReviewRequestUI: bucket === "actionNeeded" || isInactiveReReviewable,
            showDownloadUI: bucket === "active",
        };
    }
    get consentSections() {
        if (this.byStudyConsentRequest === NOT_LOADED || this.study === null)
            return [];
        const actionNeeded = [];
        const active = [];
        const inactive = [];
        for (const consent of this.allConsents) {
            const bucket = this.consentBucket(consent);
            if (bucket === null)
                continue;
            const section = this.buildConsentSection(consent, bucket);
            if (bucket === "actionNeeded")
                actionNeeded.push(section);
            else if (bucket === "active")
                active.push(section);
            else
                inactive.push(section);
        }
        return [...actionNeeded, ...active, ...inactive];
    }
    showStudyRevokeConsentDialog() {
        this.studyRevokeConsentDialogOpen = true;
        this.studyRevokeConsentStudyId = this.studyId;
    }
    hideStudyRevokeConsentDialog() {
        this.studyRevokeConsentDialogOpen = false;
        this.studyRevokeConsentStudyId = null;
    }
    startConsentFlow() {
        if (this.studyId === null)
            return;
        this.patientConsentUIManager.startConsentFlow(this.studyId, null, null);
    }
    dataUpdateConsentFlow() {
        if (!this.studyId)
            return;
        this.patientConsentUIManager.dataUpdateConsentFlow(this.studyId);
    }
}
__decorate([
    observable
], PatientResearchStudyDetailsViewModel.prototype, "studyRevokeConsentDialogOpen", void 0);
__decorate([
    observable
], PatientResearchStudyDetailsViewModel.prototype, "studyRevokeConsentStudyId", void 0);
__decorate([
    observable
], PatientResearchStudyDetailsViewModel.prototype, "byStudyConsentRequest", void 0);
__decorate([
    observable
], PatientResearchStudyDetailsViewModel.prototype, "patientStudyConsent", void 0);
__decorate([
    observable
], PatientResearchStudyDetailsViewModel.prototype, "isRevokeConsentButtonLoading", void 0);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "studyId", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "isConsentRequestLoaded", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "allConsents", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "activeConsent", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "study", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "patientFriendlyTitle", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "reasonForRequest", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "patientFriendlySummary", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "description", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "sponsorOrganizationName", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "consentFromPatientStudyConsent", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "proofOfConsentStatus", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "proofOfConsentInProgress", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "proofOfConsentStatusText", null);
__decorate([
    observable
], PatientResearchStudyDetailsViewModel.prototype, "isOpeningProofOfConsent", void 0);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "proofOfConsentUrl", null);
__decorate([
    action
], PatientResearchStudyDetailsViewModel.prototype, "openProofOfConsent", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "showProofOfConsentButton", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "proofOfConsentButtonEnabled", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "isProofOfConsentError", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "isAIProject", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "bannerInfo", null);
__decorate([
    computed
], PatientResearchStudyDetailsViewModel.prototype, "consentSections", null);
